import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthService } from 'src/app/auth/auth.service';
import { from, Observable, throwError } from 'rxjs';
import { switchMap, map, catchError, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TimezoneService } from 'src/app/core/utils/timezone.service';
import { MyAccountService } from 'src/app/core/services/my-account.service';
import { CurrencyCode } from 'src/app/core/utils/currency.utils';
import { OrderStatus } from 'src/app/shared/order-status.enum';

export enum OrderItemStatus {
  PENDING = 0,
  CLOSED = 1,
  CANCELLED = 2
}

export interface OrderResponse {
  items: OrderItem[];
  metadata: PaginationMetadata;
}

export interface OrderItem {
  id: number | string;
  orderDetails: OrderDetail[];
  centerId?: number | null;
  totalQuantity?: number;
  status: OrderStatus;
  orderDate?: string | null;
  deliveryDate?: string | null;
  receptionDate?: string | null;
  totalPrice?: number;
  storeId?: number | null;
  storeName?: string | null;
  providerId?: number | null;
  providerName?: string | null;
  serviceOrderId?: number | null;
  lastModified?: string | null;
  source?: number | null;
  sourceId?: number | null;
  currency?: CurrencyCode;
}

export interface OrderDetail {
  productId: number;
  productName: string;
  productCode?: string;
  price: number;
  quantity: number;
  currency?: CurrencyCode;
  status?: OrderStatus;
}

export interface PaginationMetadata {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  hasPrevious: boolean;
  hasNext: boolean;
  continuationToken?: string | null;
}

export interface OrderCsvRow {
  id: number | string;
  providerName?: string | null;
  storeName?: string | null;
  orderDate?: string | null;
  status?: number;
  totalPrice?: number;
  orderDetails: OrderDetail[];
  lastModified?: string | null;
  deliveryDate?: string | null;
  currency?: CurrencyCode;
}

export interface CreateOrderBody {
  orderDetails: Array<{
    productId: number;
    productName?: string | undefined;
    name?: string | undefined;
    price?: number;
    quantity: number;
    [key: string]: unknown;
  }>;
  storeId: number;
  providerId: number;
  [key: string]: unknown;
}

export interface CreateOrderResponse {
  id: number | string;
}

interface CenterInfo {
  country?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  private readonly url = environment.cafler_backoffice_back_url;
  private centerCountry = 'España';

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly timezoneService: TimezoneService,
    private readonly myAccountService: MyAccountService
  ) {
    this.loadCenterCountry();
  }

  private loadCenterCountry(): void {
    this.myAccountService
      .getCenterInfo()
      .pipe(take(1))
      .subscribe({
        next: (centerInfo: CenterInfo) => {
          this.centerCountry = centerInfo?.country ?? 'España';
        },
        error: (err) => {
          console.error('Error loading center info:', err);
          this.centerCountry = 'España';
        },
      });
  }

  private buildAuthHeaders(token: string): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  private buildParams(paramsObj: Record<string, string | number | undefined | null>): HttpParams {
    let params = new HttpParams();
    Object.keys(paramsObj).forEach((key) => {
      const value = paramsObj[key];
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });
    return params;
  }

  private toLocalString(value?: string | null): string | null {
    if (!value) return null;
    const converted = this.timezoneService.convertUtcToLocal(value, this.centerCountry);
    if (!converted) return null;
    if (converted instanceof Date) {
      return converted.toISOString();
    }
    return String(converted);
  }

  getOrders(
    page = 1,
    size = 10,
    status?: number | number[],
    orderDate?: string,
    provider?: string,
    centerId?: number,
    startDate?: string,
    endDate?: string
  ): Observable<OrderResponse> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('No token available'));
        }

        const paramsObj: Record<string, string | number | undefined | null> = {
          page: String(page),
          size: String(size),
          status: Array.isArray(status) ? status.join(',') : status,
          orderDate: orderDate ?? undefined,
          provider: provider ?? undefined,
          centerId: centerId ?? undefined,
          startDate: startDate ?? undefined,
          endDate: endDate ?? undefined,
        };

        const headers = this.buildAuthHeaders(token);
        const params = this.buildParams(paramsObj);

        return this.http.get<OrderResponse>(`${this.url}/orders`, { headers, params });
      }),
      map((response) => this.convertDatesInOrderResponse(response)),
      catchError((err) => {
        console.error('getOrders error', err);
        return throwError(() => err);
      })
    );
  }

  getOrdersCsv(
    status?: number | number[],
    orderDate?: string,
    provider?: string,
    centerId?: number,
    startDate?: string,
    endDate?: string
  ): Observable<OrderCsvRow[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('No token available'));
        }

        const paramsObj: Record<string, string | number | undefined | null> = {
          status: Array.isArray(status) ? status.join(',') : status,
          orderDate: orderDate ?? undefined,
          provider: provider ?? undefined,
          centerId: centerId ?? undefined,
          startDate: startDate ?? undefined,
          endDate: endDate ?? undefined,
        };

        const headers = this.buildAuthHeaders(token);
        const params = this.buildParams(paramsObj);

        return this.http.get<OrderCsvRow[]>(`${this.url}/orders/csv`, { headers, params });
      }),
      map((orders) => {
        if (!Array.isArray(orders)) {
          return [];
        }

        return orders.map((order) => ({
          ...order,
          orderDate: this.toLocalString(order.orderDate ?? null),
          lastModified: this.toLocalString((order).lastModified ?? null),
          deliveryDate: this.toLocalString((order).deliveryDate ?? null),
        }));
      }),
      catchError((err) => {
        console.error('getOrdersCsv error', err);
        return throwError(() => err);
      })
    );
  }

  // createOrder(body: CreateOrderBody): Observable<CreateOrderResponse> {
  //   return from(this.authService.getAccessToken()).pipe(
  //     switchMap((token) => {
  //       if (!token) {
  //         return throwError(() => new Error('No token available'));
  //       }
  //       const headers = this.buildAuthHeaders(token);
  //       return this.http.post<CreateOrderResponse>(`${this.url}/orders`, body, { headers });
  //     }),
  //     catchError((err) => {
  //       console.error('createOrder error', err);
  //       return throwError(() => err);
  //     })
  //   );
  // }

  confirmDelivery(orderId: string | number): Observable<void> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('No token available'));
        }
        const headers = this.buildAuthHeaders(token);
        return this.http.put<void>(`${this.url}/orders/${orderId}`, { status: 1 }, { headers });
      }),
      catchError((err) => {
        console.error('confirmDelivery error', err);
        return throwError(() => err);
      })
    );
  }

  cancelOrder(orderId: string | number): Observable<void> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('No token available'));
        }
        const headers = this.buildAuthHeaders(token);
        return this.http.put<void>(`${this.url}/orders/${orderId}/cancel`, {}, { headers });
      }),
      catchError((err) => {
        console.error('cancelOrder error', err);
        return throwError(() => err);
      })
    );
  }

  updateLogisticsStatus(orderId: string | number, logisticsStatus: number): Observable<void> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('No token available'));
        }
        const headers = this.buildAuthHeaders(token);
        return this.http.put<void>(`${this.url}/orders/${orderId}/logistics-status`, { logisticsStatus }, { headers });
      }),
      catchError((err) => {
        console.error('updateLogisticsStatus error', err);
        return throwError(() => err);
      })
    );
  }

  private convertDatesInOrderResponse(response: OrderResponse): OrderResponse {
    if (!response || !Array.isArray(response.items)) {
      return response;
    }

    const items = response.items.map((order) => ({
      ...order,
      orderDate: this.toLocalString(order.orderDate ?? null),
      lastModified: this.toLocalString(order.lastModified ?? null),
      deliveryDate: this.toLocalString(order.deliveryDate ?? null),
      receptionDate: this.toLocalString(order.receptionDate ?? null),
    }));

    return {
      ...response,
      items,
    };
  }

}
